import { useEffect, useState } from 'react';
import MarkdownText from './MarkdownText';

const TASK_KEY = 'task';
const MAX_TASK_LINES = 400;

function trimTaskText(text) {
  if (!text) return '';
  const lines = text.split('\n');
  if (lines.length <= MAX_TASK_LINES) return text;
  return lines.slice(0, MAX_TASK_LINES).join('\n');
}

export default function CollaborativeTaskPanel({ ydoc, editMode: editModeProp, wrapEnabled = true }) {
  const [text, setText] = useState('');
  const [activeId, setActiveId] = useState(null);
  const editMode = !!editModeProp;

  useEffect(() => {
    if (!ydoc) return;
    const m = ydoc.getMap(TASK_KEY);
    const apply = () => {
      const t = m.get('text');
      if (typeof t === 'string') setText(trimTaskText(t));
      const id = m.get('activeTaskId');
      if (typeof id === 'number') setActiveId(id);
    };
    apply();
    m.observe(apply);

    // Синхронизация текста условия с активной задачей из sessionMeta.
    // Важно: id и statement из syncActiveTaskToYdocMeta должны приходить одной транзакцией,
    // иначе при раздельных observe id уже новый, а statement ещё старый — текст «залипает».
    const sessionMeta = ydoc.getMap('sessionMeta');
    const handleMeta = (event) => {
      const activeTaskStatement = sessionMeta.get('activeTaskStatement');
      if (typeof activeTaskStatement !== 'string') return;
      const activeTaskId = sessionMeta.get('activeTaskId');
      const existingText = m.get('text');
      const currentTaskId = m.get('activeTaskId');
      const taskChanged = activeTaskId != null && activeTaskId !== currentTaskId;
      // Защита от частичных обновлений sessionMeta: id уже новый в task map,
      // а statement пришёл отдельным тиком без activeTaskId в keysChanged.
      const statementOnlyTick =
        !!event?.keysChanged?.has?.('activeTaskStatement') &&
        !event?.keysChanged?.has?.('activeTaskId') &&
        activeTaskId != null &&
        activeTaskId === currentTaskId &&
        existingText !== activeTaskStatement;

      if (!existingText || taskChanged || statementOnlyTick) {
        if (activeTaskId != null) m.set('activeTaskId', activeTaskId);
        m.set('text', activeTaskStatement);
      }
    };
    sessionMeta.observe(handleMeta);
    handleMeta();

    return () => {
      m.unobserve(apply);
      sessionMeta.unobserve(handleMeta);
    };
  }, [ydoc]);

  const handleTextChange = (e) => {
    const value = trimTaskText(e.target.value);
    setText(value);
    if (ydoc) {
      const m = ydoc.getMap(TASK_KEY);
      m.set('text', value);
    }
  };

  const updateSelectionFromDom = (e) => {
    if (!editMode) return;
    const ta = e?.target;
    if (!ta || typeof ta.selectionStart !== 'number' || typeof ta.selectionEnd !== 'number') return;
    let start = Math.max(0, ta.selectionStart);
    let end = Math.max(0, ta.selectionEnd);
    if (start > end) [start, end] = [end, start];
    const s = { start, end };
    if (ydoc) {
      const m = ydoc.getMap(TASK_KEY);
      m.set('selectionStart', s.start);
      m.set('selectionEnd', s.end);
    }
  };

  return (
    <div className="collab-task-panel">
      <div className="collab-task-display-wrapper">
        {editMode ? (
          <textarea
            value={text}
            onChange={handleTextChange}
            onMouseUp={updateSelectionFromDom}
            onKeyUp={updateSelectionFromDom}
            rows={10}
            className={`output-block collab-task-editor ${wrapEnabled ? 'task-text-wrap' : 'task-text-nowrap'}`}
            placeholder="Вставьте или введите текст условия..."
          />
        ) : (
          <div
            className={`output-block collab-task-display collab-task-markdown-host ${wrapEnabled ? 'task-text-wrap' : 'task-text-nowrap'}`}
          >
            <div key={String(activeId ?? 'none')} className="collab-task-switch-anim">
              <MarkdownText
                text={text || ''}
                className={`statement-text ${wrapEnabled ? 'task-text-wrap' : 'task-text-nowrap'}`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
